/**
 * DocumentWorkbench — the left pane of the expediente detail: one large
 * zoomable stage for the active document plus a thumbnail strip to switch
 * between the expediente's documents (certificado first — the review order).
 *
 * `DocumentRef.url` stays OPAQUE end to end. The stage is keyed by
 * `${object_name}:${url}` so a retry (fresh signed URLs) remounts it and
 * resets its failed/zoom state.
 */

import { useMemo, useState } from "react";
import { Alert, Card, CardContent, Link, Stack, Typography } from "@mui/material";

import type { ApiError, DocumentRef } from "../../api/client";
import { Loading } from "../../components/Loading";
import { ProblemAlert } from "../../components/ProblemAlert";
import { EmptyState } from "../../components/EmptyState";
import { DocumentStage } from "./DocumentStage";
import { ThumbnailStrip } from "./ThumbnailStrip";
import { KIND_ORDER, labelForTipo } from "./docLabels";
import { isSingleIdentificationSource, SINGLE_IDENTIFICATION_NOTICE } from "./identification";

export function DocumentWorkbench({
  documents,
  loading,
  error,
  onRetry,
}: {
  documents: DocumentRef[] | null;
  loading: boolean;
  error: ApiError | Error | null;
  onRetry?: () => void;
}) {
  const ordered = useMemo(() => {
    if (!documents) return [];
    return [...documents].sort(
      (a, b) => KIND_ORDER.indexOf(a.tipo) - KIND_ORDER.indexOf(b.tipo),
    );
  }, [documents]);

  const [selectedTipo, setSelectedTipo] = useState<DocumentRef["tipo"] | null>(null);
  // Selected if still present; otherwise the first in review order.
  const active =
    ordered.find((d) => d.tipo === selectedTipo) ?? ordered[0] ?? null;

  // ADR 0007 / #275: one identification file filled both INE slots — say so,
  // or the identical frente/reverso pair reads as a bug.
  const singleIdentification = isSingleIdentificationSource(documents);

  if (loading) {
    return <Loading label="Cargando documentos…" />;
  }

  if (error) {
    // 401/403 are handled by the route guards; pass everything else through.
    return <ProblemAlert error={error} onRetry={onRetry} />;
  }

  if (!active) {
    return (
      <EmptyState
        title="Sin documentos"
        description="No hay documentos disponibles para este expediente."
      />
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="subtitle2">{labelForTipo(active.tipo)}</Typography>
          <Link
            href={active.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
          >
            Abrir en pestaña nueva
          </Link>
        </Stack>

        {singleIdentification && (
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            {SINGLE_IDENTIFICATION_NOTICE}
          </Alert>
        )}

        <DocumentStage
          // Keyed remount: fresh signed URLs reset failed/zoom state.
          key={`${active.object_name}:${active.url}`}
          doc={active}
          label={labelForTipo(active.tipo)}
          onRetry={onRetry}
        />

        {ordered.length > 1 && (
          <ThumbnailStrip
            documents={ordered}
            activeTipo={active.tipo}
            onSelect={setSelectedTipo}
          />
        )}
      </CardContent>
    </Card>
  );
}
